import React, { useState, useMemo } from 'react';

const FinancialReports = ({ dashboardData, dateFilter }) => {
    // Active tab state
    const [activeTab, setActiveTab] = useState('daily');

    // Helper function to format date
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Helper function to get day name
    const getDayName = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR', { weekday: 'short' });
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        return (parseFloat(amount || 0)).toLocaleString('tr-TR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    // Process data based on active tab
    const processedData = useMemo(() => {
        const financialData = dashboardData?.financialSummary || {};
        const currentPeriodData = financialData[activeTab] || {};
        const dailyData = currentPeriodData.dailyData || [];

        if (activeTab === 'daily') {
            // For daily view, show last 7 days
            return dailyData
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 7); // Last 7 days
        }

        if (activeTab === 'weekly') {
            // Group by week
            const weeksMap = {};
            dailyData.forEach(day => {
                const date = new Date(day.date);
                const weekStart = new Date(date);
                weekStart.setDate(date.getDate() - date.getDay());
                const weekKey = weekStart.toISOString().split('T')[0];

                if (!weeksMap[weekKey]) {
                    weeksMap[weekKey] = {
                        date: weekKey,
                        total_income: 0,
                        total_expense: 0,
                        net_amount: 0,
                        days: []
                    };
                }
                weeksMap[weekKey].total_income += parseFloat(day.total_income || 0);
                weeksMap[weekKey].total_expense += parseFloat(day.total_expense || 0);
                weeksMap[weekKey].net_amount += parseFloat(day.net_amount || 0);
                weeksMap[weekKey].days.push(day);
            });

            return Object.values(weeksMap)
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 4); // Last 4 weeks
        }

        if (activeTab === 'monthly') {
            // Group by month
            const monthsMap = {};
            dailyData.forEach(day => {
                const date = new Date(day.date);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!monthsMap[monthKey]) {
                    monthsMap[monthKey] = {
                        date: monthKey + '-01',
                        total_income: 0,
                        total_expense: 0,
                        net_amount: 0,
                        days: []
                    };
                }
                monthsMap[monthKey].total_income += parseFloat(day.total_income || 0);
                monthsMap[monthKey].total_expense += parseFloat(day.total_expense || 0);
                monthsMap[monthKey].net_amount += parseFloat(day.net_amount || 0);
                monthsMap[monthKey].days.push(day);
            });

            return Object.values(monthsMap)
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 3); // Last 3 months
        }

        if (activeTab === 'yearly') {
            // Group by year
            const yearsMap = {};
            dailyData.forEach(day => {
                const date = new Date(day.date);
                const yearKey = date.getFullYear().toString();

                if (!yearsMap[yearKey]) {
                    yearsMap[yearKey] = {
                        date: yearKey + '-01-01',
                        total_income: 0,
                        total_expense: 0,
                        net_amount: 0,
                        days: []
                    };
                }
                yearsMap[yearKey].total_income += parseFloat(day.total_income || 0);
                yearsMap[yearKey].total_expense += parseFloat(day.total_expense || 0);
                yearsMap[yearKey].net_amount += parseFloat(day.net_amount || 0);
                yearsMap[yearKey].days.push(day);
            });

            return Object.values(yearsMap)
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 2); // Last 2 years
        }

        return [];
    }, [dashboardData, activeTab]);

    // Calculate summary metrics with dynamic period info
    const summaryMetrics = useMemo(() => {
        // Get the latest period data (first item in sorted array)
        const latestData = processedData.length > 0 ? processedData[0] : null;

        if (!latestData) {
            return {
                totalIncome: 0,
                totalExpense: 0,
                netAmount: 0,
                profitMargin: 0,
                periodTitle: '',
                periodInfo: '',
                dataCount: 0
            };
        }

        const totalIncome = parseFloat(latestData.total_income || latestData.totalIncome || 0);
        const totalExpense = parseFloat(latestData.total_expense || latestData.totalExpense || 0);
        const netAmount = parseFloat(latestData.net_amount || latestData.netAmount || 0);
        const profitMargin = totalIncome > 0 ? ((netAmount / totalIncome) * 100) : (netAmount > 0 ? 100 : 0);

        // Get period info based on active tab
        let periodTitle = '';
        let periodInfo = '';

        if (activeTab === 'daily') {
            const date = new Date(latestData.date);
            periodTitle = date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            periodInfo = date.toLocaleDateString('tr-TR', { weekday: 'long' });
        } else if (activeTab === 'weekly') {
            const date = new Date(latestData.date);
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            const weekNumber = Math.ceil((((weekStart - new Date(weekStart.getFullYear(), 0, 1)) / 86400000) + weekStart.getDay() + 1) / 7);
            periodTitle = `${weekNumber}. Hafta`;
            periodInfo = `${weekStart.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })} - ${weekEnd.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}`;
        } else if (activeTab === 'monthly') {
            const date = new Date(latestData.date);
            periodTitle = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
            periodInfo = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        } else if (activeTab === 'yearly') {
            const date = new Date(latestData.date);
            periodTitle = date.getFullYear().toString();
            periodInfo = 'Yıllık Toplam';
        }

        return {
            totalIncome,
            totalExpense,
            netAmount,
            profitMargin,
            periodTitle,
            periodInfo,
            dataCount: processedData.length
        };
    }, [processedData, activeTab]);

    // Render period name for table
    const renderPeriodName = (item) => {
        const date = new Date(item.date);

        if (activeTab === 'daily') {
            return (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                        {getDayName(item.date)} {formatDate(item.date)}
                    </span>
                </div>
            );
        }

        if (activeTab === 'weekly') {
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            const weekNumber = Math.ceil((((weekStart - new Date(weekStart.getFullYear(), 0, 1)) / 86400000) + weekStart.getDay() + 1) / 7);

            return (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                        {weekNumber}. Hafta
                    </span>
                    <span className="text-xs text-gray-500">
                        {weekStart.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })} - {weekEnd.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                    </span>
                </div>
            );
        }

        if (activeTab === 'monthly') {
            const monthName = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
            return (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900">{monthName}</span>
                </div>
            );
        }

        if (activeTab === 'yearly') {
            return (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900">{date.getFullYear()}</span>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">
                    FİNANSAL RAPORLAR
                </h2>
            </div>

            {/* Summary Cards */}
            <div className="mb-8">
                <div className="mb-3">
                    <h3 className="text-sm font-bold text-gray-700 tracking-wide">
                        {summaryMetrics.periodTitle} - Finansal Özeti
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                        {summaryMetrics.periodInfo}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* Total Income */}
                    <div className="group relative overflow-hidden">
                        <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                            <p className="text-xs font-bold text-gray-700 mb-2 tracking-wide">
                                TOPLAM GELİR
                            </p>
                            <p className="text-2xl font-black text-gray-900 tracking-tight mb-2">
                                {formatCurrency(summaryMetrics.totalIncome)} ₺
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                                {summaryMetrics.periodInfo}
                            </p>
                        </div>
                    </div>

                    {/* Total Expense */}
                    <div className="group relative overflow-hidden">
                        <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                            <p className="text-xs font-bold text-gray-700 mb-2 tracking-wide">
                                TOPLAM GİDER
                            </p>
                            <p className="text-2xl font-black text-gray-900 tracking-tight mb-2">
                                {formatCurrency(summaryMetrics.totalExpense)} ₺
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                                {summaryMetrics.periodInfo}
                            </p>
                        </div>
                    </div>

                    {/* Net Amount */}
                    <div className="group relative overflow-hidden">
                        <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                            <p className="text-xs font-bold text-gray-700 mb-2 tracking-wide">
                                NET
                            </p>
                            <p className={`text-2xl font-black tracking-tight mb-2 ${summaryMetrics.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(summaryMetrics.netAmount)} ₺
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                                {summaryMetrics.periodInfo}
                            </p>
                        </div>
                    </div>

                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <div className="flex">
                    <button
                        onClick={() => setActiveTab('daily')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-all ${
                            activeTab === 'daily'
                                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        Günlük
                    </button>
                    <button
                        onClick={() => setActiveTab('weekly')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-all ${
                            activeTab === 'weekly'
                                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        Haftalık
                    </button>
                    <button
                        onClick={() => setActiveTab('monthly')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-all ${
                            activeTab === 'monthly'
                                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        Aylık
                    </button>
                    <button
                        onClick={() => setActiveTab('yearly')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-all ${
                            activeTab === 'yearly'
                                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        Yıllık
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Dönem
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Gelir
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Gider
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Net
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Durum
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {processedData.length > 0 ? (
                                processedData.map((item, index) => (
                                    <tr key={item.date} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            {renderPeriodName(item)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-right">
                                            <span className="text-sm font-semibold text-primary-600">
                                                +{formatCurrency(item.total_income || item.totalIncome)} ₺
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-right">
                                            <span className="text-sm font-semibold text-orange-600">
                                                -{formatCurrency(item.total_expense || item.totalExpense)} ₺
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <span className={`text-sm font-bold ${(parseFloat(item.net_amount || item.netAmount || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatCurrency(item.net_amount || item.netAmount)} ₺
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-right">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(parseFloat(item.net_amount || item.netAmount || 0)) >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {(parseFloat(item.net_amount || item.netAmount || 0)) >= 0 ? 'Pozitif' : 'Negatif'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <p className="text-sm text-gray-600 font-medium">Bu dönem için finansal veri bulunamadı</p>
                                            <p className="text-xs text-gray-500 mt-1">Finansal işlemler yaptığınızda burada görünecek</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Quick Stats Footer */}
                {processedData.length > 0 && (
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-6">
                                <div>
                                    <span className="text-gray-600">Gösterilen Dönem: </span>
                                    <span className="font-bold text-gray-900">{summaryMetrics.periodTitle}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Toplam Kayıt: </span>
                                    <span className="font-bold text-gray-900">{summaryMetrics.dataCount} adet</span>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                Son güncelleme: {new Date().toLocaleString('tr-TR')}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinancialReports;
